import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense, useReducer } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, FileText, ChevronRight, Undo2, PenSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ACADEMY_CUTOVER } from '../utils/roles';
import ProblemPreviewCards from '../components/ProblemPreviewCards';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  LabelList,
  Sector
} from 'recharts';

// 공통 UI 컴포넌트 import
import {
  PageContainer,
  Card,
  Button,
  IconButton,
  SectionHeader,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  Modal,
  ConfirmModal,
  BypassLayout
} from '../components/common';
import FloatingButton from '../components/common/FloatingButton';

// LearningStatus 컴포넌트 import
import {
  StudentCard,
  TestHistoryItem,
  StudentSection,
  TestHistoryPanel,
  FormRatingChartSection
} from './LearningStatus/components';
import DifficultyAccuracyChartSection from './LearningStatus/components/DifficultyAccuracyChartSection';
import DifficultyAchievementChartSection from './LearningStatus/components/DifficultyAchievementChartSection';
import { COLORS } from './LearningStatus/components/FormRatingChartSection';

// 상수와 헬퍼 함수 import
import { DIFFICULTY_COLORS, ITEMS_PER_PAGE, TESTS_PER_PAGE } from './LearningStatus/utils/constants';
import { getReportStatusInfo, getTestStatusInfo, getStatusColor, decodeHtml, formatDateTime } from './LearningStatus/utils/helpers';

// 디자인 토큰 import
import { HEX_COLORS, DIFFICULTY_COLORS as LEVEL_COLORS } from '../styles/tokens';

// 커스텀 훅 import
import { useTests } from './LearningStatus/hooks/useTests';
import { useBypassLayout } from '../hooks/useBypassLayout';
import { LearningStatusDesktop, createLearningStatusAreas } from './LearningStatus/LearningStatusDesktop';

// Y축 레이블용 진한 색상 (폼레이팅 변화 추이와 동일하게)
const Y_AXIS_LABEL_COLORS = [
  '#EAB308',  // Level 1 - yellow-600 (진한 노란색)
  '#059669',  // Level 2 - emerald-600 (진한 녹색)
  '#2563EB',  // Level 3 - blue-600 (진한 파란색)
  '#EA580C',  // Level 4 - orange-600 (진한 주황색)
  '#DC2626'   // Level 5 - red-600 (진한 빨간색)
];

// Y축 레이블용 색상 결정 함수
const getYAxisLabelColor = (value, isRating = true) => {
  if (isRating) {
    // 폼레이팅 Y축
    if (!value || value === null || value === 0) return Y_AXIS_LABEL_COLORS[0]; // 0 또는 null일 때도 Lv1 색상
    if (value <= 2000) return Y_AXIS_LABEL_COLORS[0]; // Lv1 진한 색상
    if (value <= 4000) return Y_AXIS_LABEL_COLORS[1]; // Lv2 진한 색상
    if (value <= 6000) return Y_AXIS_LABEL_COLORS[2]; // Lv3 진한 색상
    if (value <= 8000) return Y_AXIS_LABEL_COLORS[3]; // Lv4 진한 색상
    return Y_AXIS_LABEL_COLORS[4]; // Lv5 진한 색상
  } else {
    // 정답률 Y축 (녹색 계열로 표시)
    return COLORS.primary; // primary 색상 사용
  }
};

// 레이지 로딩 적용 - 모달 컴포넌트들만
const ProblemPreview = lazy(() => import('../components/ProblemPreview'));
const PrintPreviewModal = lazy(() => import('../components/printPreview').then(module => ({ default: module.PrintPreviewModal })));
const ReportModal = lazy(() => import('../components/ReportModal'));
const StudentModal = lazy(() => import('../components/StudentManagement/StudentModal'));

function LearningStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken, userType, userId } = useAuth();
  
  // ==================== 상태 관리 최적화 ====================
  
  /**
   * 하이라이트 관련 상태 통합 (3개 상태 -> 1개 객체)
   * - address: 결과맵에서 하이라이트된 주소
   * - problemIndex: 문제 리스트에서 선택된 인덱스
   * - difficulty: 하이라이트된 난이도 레벨
   */
  const [highlightState, dispatchHighlight] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_ALL':
          return { ...state, ...action.payload };
        case 'RESET':
          return { address: null, problemIndex: null, difficulty: null };
        default:
          return state;
      }
    },
    { address: null, problemIndex: null, difficulty: null }
  );

  /**
   * 모달 상태 통합 (6개 상태 -> 1개 객체)
   * 모든 모달의 열림/닫힘 상태를 하나의 객체로 관리
   */
  const [modalState, dispatchModal] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'OPEN_MODAL':
          return { ...state, [action.payload]: true };
        case 'CLOSE_MODAL':
          return { ...state, [action.payload]: false };
        case 'TOGGLE_MODAL':
          return { ...state, [action.payload]: !state[action.payload] };
        case 'CLOSE_ALL':
          return Object.keys(state).reduce((acc, key) => ({ ...acc, [key]: false }), {});
        default:
          return state;
      }
    },
    {
      problemPreview: false,
      printPreview: false,
      report: false,
      teacherAssign: false,
      resetPassword: false,
      deleteConfirm: false,
      passwordPopup: false
    }
  );

  /**
   * 문제 미리보기 관련 상태 통합 (3개 상태 -> 1개 객체)
   */
  const [previewState, setPreviewState] = useReducer(
    (state, action) => ({ ...state, ...action }),
    { problemId: null, correct: null, number: null }
  );

  /**
   * 학생 정보 모달 관련 상태 통합
   */
  const [studentModalState, setStudentModalState] = useReducer(
    (state, action) => ({ ...state, ...action }),
    {
      teachers: [],
      selectedTeacherId: '',
      newPassword: '',
      deleteEmail: '',
      deleteError: ''
    }
  );

  // 기존 단일 상태들 (통합 대상 아님)
  const [resultMapData, setResultMapData] = useState(null);
  const [isResultMapLoading, setIsResultMapLoading] = useState(false);
  const [visibleHierarchyLevel, setVisibleHierarchyLevel] = useState(3);
  const [reportData, setReportData] = useState(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [currentReportStampId, setCurrentReportStampId] = useState(null);
  
  // 도넛 차트 인터랙션을 위한 상태
  const [activePieIndex, setActivePieIndex] = useState(-1);
  
  // 스크롤 위치 저장을 위한 ref 추가
  const currentScrollPositions = useRef({
    area3: 0,
    resultMap: 0,
    problemList: {}
  });
  const area3ScrollRef = useRef(null);
  const isNavigating = useRef(false);
  
  // BypassLayout 커스텀 훅 사용
  const { 
    isMobileView, 
    currentMobilePanel, 
    setCurrentMobilePanel, 
    windowWidth, 
    handleMobileBack, 
    isInitialized,
    isIOS,
    isIPhone,
    isSafari,
    getMobileContainerHeight,
    getMobilePanelHeight,
    // URL 파라미터
    studentId,
    testId,
    // 페이지 전환 함수들
    navigateToStudent,
    navigateToTest,
    navigateToStudentList
  } = useBypassLayout({
    enableMobilePanels: true,
    basePath: '/learning-status'
  });
  
  // 학생 상세 정보 모달 상태
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  const [isLoadingStudentDetail, setIsLoadingStudentDetail] = useState(false);
  
  // 학생 정보 수정 관련 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingStudent, setEditingStudent] = useState(null); // 수정용 학생 데이터
  
  // URL 파라미터를 기반으로 학생 선택 상태 확인
  const isStudentSelected = !!studentId;
  
  // 선택된 학생 정보 상태 (StudentSection에서 전달받음)
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // URL 직접 접근을 위한 추가 상태
  const [isLoadingDirectAccess, setIsLoadingDirectAccess] = useState(false);
  const [directAccessError, setDirectAccessError] = useState(null);
  
  // URL에서 studentId가 없어지면 selectedStudent 초기화
  useEffect(() => {
    if (!studentId && selectedStudent) {
      setSelectedStudent(null);
    }
  }, [studentId, selectedStudent]);
  
  /**
   * ==================== URL 직접 접근 전용 로직 ====================
   * 
   * 책임: URL을 통해 직접 학생 페이지에 접근한 경우에만 학생 정보를 조회
   * - /learning-status/student/123 형태로 직접 접근
   * - 브라우저 새로고침, 북마크, 링크 공유 등
   * 
   * StudentSection은 학생 목록에서 선택 시 즉시 상태를 업데이트하므로
   * 이 로직이 실행되지 않음 (중복 API 호출 방지)
   */
  const directAccessCacheRef = useRef(new Map());
  const fetchingDirectAccessRef = useRef(new Set());
  
  const fetchStudentForDirectAccess = useCallback(async (targetStudentId) => {
    const numericStudentId = parseInt(targetStudentId);
    
    // 캐시에서 먼저 확인
    if (directAccessCacheRef.current.has(numericStudentId)) {
      console.log(`직접 접근 학생 ID ${targetStudentId} 캐시에서 반환`);
      return directAccessCacheRef.current.get(numericStudentId);
    }
    
    // 이미 조회 중이면 건너뛰기
    if (fetchingDirectAccessRef.current.has(numericStudentId)) {
      console.log(`학생 ID ${targetStudentId} 이미 조회 중`);
      return null;
    }

    fetchingDirectAccessRef.current.add(numericStudentId);
    
    try {
      setIsLoadingDirectAccess(true);
      setDirectAccessError(null);
      
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      console.log(`URL 직접 접근: 학생 ID ${targetStudentId} 조회 시작...`);

      // 새로운 단일 학생 조회 API 사용
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/card?student_id=${numericStudentId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 404) {
        console.error(`학생 ID ${targetStudentId}를 찾을 수 없습니다.`);
        // 404 시 이전 페이지로 이동
        navigate(-1);
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        const studentData = result.data;
        
        // 캐시에 저장
        directAccessCacheRef.current.set(studentData.student_id, studentData);
        
        // 필드명 매핑 (API 응답과 기존 코드의 필드명이 다를 수 있음)
        const normalizedStudent = {
          student_id: studentData.student_id,
          student_name: studentData.student_name,
          student_email: studentData.student_email,
          school_grade: studentData.student_grade || studentData.school_grade,
          school_name: studentData.school_name,
          mobile_number: studentData.mobile_number,
          parent_mobile_number: studentData.parent_mobile_number,
          teacher_name: studentData.in_charge_teacher,
          in_charge_teacher: studentData.in_charge_teacher,
          lecture: studentData.lecture,
          acq_channel: studentData.acq_channel,
          referral_id: studentData.referral_id,
          is_blocked: studentData.is_blocked,
          created_at: studentData.created_at,
          updated_at: studentData.updated_at
        };
        
        console.log(`✅ 직접 접근 학생 ID ${targetStudentId} 조회 성공`);
        return normalizedStudent;
      }

      throw new Error(`학생 정보 형식이 올바르지 않습니다.`);
    } catch (error) {
      console.error('Direct access student fetch error:', error);
      setDirectAccessError(error.message);
      return null;
    } finally {
      fetchingDirectAccessRef.current.delete(numericStudentId);
      setIsLoadingDirectAccess(false);
    }
  }, [getAccessToken, navigate]); // 의존성 단순화
  
  /**
   * URL 직접 접근 처리 (LearningStatus의 핵심 책임)
   * 
   * 실행 조건:
   * - studentId 존재 (URL에 student ID 포함)
   * - selectedStudent 없음 (아직 로드되지 않음)  
   * - 로딩 중 아님
   * 
   * 학생 목록에서 클릭한 경우에는 이미 selectedStudent가 설정되어 있으므로
   * 이 useEffect는 실행되지 않음
   */
  useEffect(() => {
    if (studentId && !selectedStudent && !isLoadingDirectAccess) {
      console.log('🔗 URL 직접 접근 감지, 학생 정보 로드 시작:', studentId);
      
      fetchStudentForDirectAccess(studentId).then(student => {
        if (student) {
          setSelectedStudent(student);
          setDirectAccessError(null);
          console.log('✅ URL 직접 접근 학생 로드 완료:', student.student_name);
        }
      });
    }
    // fetchStudentForDirectAccess를 의존성에서 제거 (무한 루프 방지)
  }, [studentId, selectedStudent, isLoadingDirectAccess]);
  
  const {
    testHistory,
    isTestHistoryLoading,
    testHistoryPage,
    hasMoreTests,
    testHistoryRef,
    fetchTestHistory,
    loadMoreTests
  } = useTests(selectedStudent, currentMobilePanel);
  
  // URL 파라미터를 기반으로 선택된 테스트 찾기
  const selectedTest = testId && testHistory ? testHistory.find(test => test.exam_id === parseInt(testId)) : null;
  
  // ref들
  const resultMapTableRef = useRef(null);
  const resultMapTableMobileRef = useRef(null);
  const rowRefs = useRef({});
  const studentListRef = useRef(null);
  
  // 스크롤 위치 저장 함수 (세로 스크롤만)
  const saveScrollPosition = useCallback(() => {
    try {
      // area3 전체 세로 스크롤 위치 저장 (모바일만)
      if (isMobileView && area3ScrollRef.current) {
        currentScrollPositions.current.area3 = area3ScrollRef.current.scrollTop;
      }
      
      // 결과맵 테이블 세로 스크롤 위치만 저장
      const resultMapTable = isMobileView ? resultMapTableMobileRef.current : resultMapTableRef.current;
      if (resultMapTable) {
        currentScrollPositions.current.resultMap = resultMapTable.scrollTop;
      }
      
      // 문제 리스트 세로 스크롤 위치만 저장
      const problemListTables = document.querySelectorAll('.problem-list-table');
      problemListTables.forEach((table, index) => {
        if (table && table.scrollTop !== undefined) {
          currentScrollPositions.current.problemList[index] = table.scrollTop;
        }
      });
      

    } catch (error) {
      console.warn('스크롤 위치 저장 실패:', error);
    }
  }, [isMobileView]);
  
  // 스크롤 위치 복원 함수 (세로 스크롤만)
  const restoreScrollPosition = useCallback(() => {
    if (!isNavigating.current) return;
    
    try {
      // requestAnimationFrame을 사용하여 DOM 업데이트 후 스크롤 복원
      requestAnimationFrame(() => {
        setTimeout(() => {
          // area3 전체 세로 스크롤 위치 복원 (모바일만)
          if (isMobileView && area3ScrollRef.current && currentScrollPositions.current.area3 !== undefined) {
            area3ScrollRef.current.scrollTop = currentScrollPositions.current.area3;
          }
          
          // 결과맵 테이블 세로 스크롤 위치만 복원
          const resultMapTable = isMobileView ? resultMapTableMobileRef.current : resultMapTableRef.current;
          if (resultMapTable && currentScrollPositions.current.resultMap !== undefined) {
            resultMapTable.scrollTop = currentScrollPositions.current.resultMap;
          }
          
          // 문제 리스트 세로 스크롤 위치만 복원
          const problemListTables = document.querySelectorAll('.problem-list-table');
          problemListTables.forEach((table, index) => {
            const savedPosition = currentScrollPositions.current.problemList[index];
            if (table && savedPosition !== undefined) {
              table.scrollTop = savedPosition;
            }
          });
          

          
          // 네비게이션 완료
          isNavigating.current = false;
        }, 50); // 짧은 지연으로 DOM 업데이트 완료 대기
      });
    } catch (error) {
      console.warn('스크롤 위치 복원 실패:', error);
      isNavigating.current = false;
    }
  }, [isMobileView]);
  
  // 향상된 navigateToTest 함수
  const enhancedNavigateToTest = useCallback((student, test) => {
    // 현재 스크롤 위치 저장
    saveScrollPosition();
    
    // 네비게이션 시작
    isNavigating.current = true;
    
    // URL 변경
    navigateToTest(student, test);
  }, [saveScrollPosition, navigateToTest]);



  // 결과맵 데이터 가져오기
  const fetchResultMap = async (examId) => {
    if (!examId) return;

    try {
      setIsResultMapLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/resultmap?exam_id=${examId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResultMapData(data.data);
    } catch (error) {
      console.error('Error fetching result map:', error);
      setResultMapData(null);
    } finally {
      setIsResultMapLoading(false);
    }
  };

  // 선택된 학생이 변경될 때 리포트 데이터 초기화
  useEffect(() => {
    if (selectedStudent) {
      setReportData(null); // 리포트 데이터 초기화
      setCurrentReportStampId(null); // 현재 리포트 stamp 초기화
    } else {
      setReportData(null);
      setCurrentReportStampId(null);
    }
  }, [selectedStudent]);

  // 선택된 테스트가 변경될 때 결과맵 데이터 로딩
  useEffect(() => {
    if (selectedTest) {
      fetchResultMap(selectedTest.exam_id);
      // 하이라이트 상태 초기화 - 통합된 상태로 한 번에 처리
      dispatchHighlight({ type: 'RESET' });
      
      // 도넛 차트에서 가장 큰 값을 가진 영역 자동 선택
      const pieData = getLevelPieData(selectedTest);
      if (pieData && pieData.length > 0) {
        let maxIndex = 0;
        let maxValue = pieData[0].value;
        
        for (let i = 1; i < pieData.length; i++) {
          if (pieData[i].value > maxValue) {
            maxValue = pieData[i].value;
            maxIndex = i;
          }
        }
        
        setActivePieIndex(maxIndex);
      }
    } else {
      // setResultMapData(null); // 깜박임 방지를 위해 주석 처리
      // 하이라이트 상태 초기화
      dispatchHighlight({ type: 'RESET' });
      setActivePieIndex(-1); // 도넛 차트 선택 해제
    }
  }, [selectedTest]);
  
  // 테스트 변경 시 스크롤 위치 복원
  useEffect(() => {
    if (isNavigating.current) {
      // DOM 업데이트를 기다린 후 스크롤 복원
      setTimeout(() => {
        restoreScrollPosition();
      }, 100);
    }
  }, [selectedTest, restoreScrollPosition]);
  
  // URL 직접 접근 시 테스트가 로드되면 스크롤 실행을 위한 추가 지연
  useEffect(() => {
    if (selectedTest && testId && testHistory.length > 0) {
      // URL에서 직접 접근했을 때 테스트가 로드된 경우
      // TestHistoryPanel의 스크롤이 완료될 시간을 추가로 확보
      console.log('📍 URL 직접 접근 테스트 로드 완료, 스크롤 준비:', selectedTest.exam_id);
    }
  }, [selectedTest?.exam_id, testId, testHistory.length]);

  // URL 접근 시 제한된 데이터 로드 관리
  const [isUrlMode, setIsUrlMode] = useState(false);
  const [loadedForFirstTest, setLoadedForFirstTest] = useState(false);
  
  // URL로 접근했는지 감지
  useEffect(() => {
    if (testId && !selectedTest && selectedStudent) {
      setIsUrlMode(true);
      console.log(`URL 모드 활성화: TID ${testId}를 찾기 위해 데이터를 로드합니다.`);
    } else if (selectedTest) {
      // 테스트를 찾으면 URL 모드 해제
      setIsUrlMode(false);
    }
  }, [testId, selectedStudent, selectedTest]);
  
  // testId가 있지만 테스트를 찾지 못했을 때 제한적으로 테스트 로드
  useEffect(() => {
    // 디버깅 로그 추가
    console.log('🔍 테스트 로드 조건 확인:', {
      testId,
      hasSelectedStudent: !!selectedStudent,
      selectedTest: selectedTest?.exam_id,
      isTestHistoryLoading,
      hasMoreTests,
      testHistoryLength: testHistory.length,
      currentTests: testHistory.map(t => ({ id: t.exam_id, status: t.exam_status }))
    });
    
    if (testId && selectedStudent && !selectedTest && !isTestHistoryLoading && hasMoreTests) {
      // URL에서 직접 접근한 경우 해당 테스트를 찾을 때까지 계속 로드
      const targetTestId = parseInt(testId);
      const foundTest = testHistory.find(test => test.exam_id === targetTestId);
      
      if (!foundTest) {
        console.log(`📍 TID ${testId}를 찾기 위해 추가 데이터를 로드합니다... (현재: ${testHistory.length}개)`);
        loadMoreTests();
      } else {
        console.log(`✅ TID ${testId} 찾음!`, foundTest);
      }
    }
  }, [testId, selectedStudent, selectedTest, isTestHistoryLoading, hasMoreTests, testHistory]);
  
  // selectedTest가 첫 번째 테스트일 때 자동으로 5개 추가 로드
  useEffect(() => {
    if (selectedTest && testHistory.length > 0 && !loadedForFirstTest) {
      // 시간순 정렬 (과거 → 현재)
      const sortedTests = [...testHistory]
        .filter(test => test.stamp_id === selectedTest.stamp_id)
        .sort((a, b) => {
          const dateA = new Date(a.created_at || a.exam_created_at || a.updated_at);
          const dateB = new Date(b.created_at || b.exam_created_at || b.updated_at);
          return dateA - dateB;
        });
      
      // 선택된 테스트가 첫 번째 테스트인지 확인
      if (sortedTests.length > 0 && sortedTests[0].exam_id === selectedTest.exam_id && hasMoreTests) {
        console.log('첫 번째 테스트가 선택됨: 이전 5개 데이터를 추가로 로드합니다...');
        setLoadedForFirstTest(true);
        
        // 5개 로드를 위해 loadMoreTests를 호출
        // useTests 훅에서 한 번에 10개씩 로드하므로, 한 번만 호출
        loadMoreTests();
      }
    }
    // loadMoreTests를 의존성에서 제거 (무한 루프 방지)
  }, [selectedTest, testHistory, hasMoreTests, loadedForFirstTest]);
  
  // selectedTest가 변경되면 플래그 초기화
  useEffect(() => {
    setLoadedForFirstTest(false);
  }, [selectedTest?.exam_id]);
  

  // 하이라이팅된 주소로 자동 스크롤
  useEffect(() => {
    if (highlightState.address && rowRefs.current[highlightState.address]) {
      const element = rowRefs.current[highlightState.address];
      const container = resultMapTableRef.current || resultMapTableMobileRef.current;
      
      if (element && container) {
        // 테이블 컨테이너 내에서의 상대적 위치 계산
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // 요소가 뷰포트 중앙에 오도록 스크롤 위치 계산
        const elementTop = element.offsetTop;
        const elementHeight = element.offsetHeight;
        const containerHeight = container.clientHeight;
        const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
        
        // 부드럽게 스크롤
        container.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  }, [highlightState.address]);

  // 학생 상세 정보 모달 핸들러 - 메모이제이션으로 리렌더링 최적화
  const handleStudentDetailClick = useCallback(async (student) => {
    try {
      // 로딩 상태 시작
      setIsLoadingStudentDetail(true);
      setSelectedStudentDetail(student); // 일단 기존 데이터로 설정
      setShowDetailModal(true); // 모달을 먼저 열어서 로딩 표시
      
      // 모달을 열기 전에 최신 학생 정보를 가져옴
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 학생 ID로 최신 정보 조회 (새로운 단일 학생 API 사용)
      const fetchLatestStudentInfo = async (studentId) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/student/card?student_id=${studentId}`,
            {
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json',
              }
            }
          );

          if (!response.ok) {
            console.error(`학생 정보 조회 실패: ${response.status}`);
            return null;
          }

          const result = await response.json();
          
          if (result.status === 'success' && result.data) {
            const studentData = result.data;
            
            // 필드명 매핑 (API 응답과 기존 코드의 필드명이 다를 수 있음)
            return {
              student_id: studentData.student_id,
              student_name: studentData.student_name,
              student_email: studentData.student_email,
              school_grade: studentData.student_grade || studentData.school_grade,
              school_name: studentData.school_name,
              mobile_number: studentData.mobile_number,
              parent_mobile_number: studentData.parent_mobile_number,
              teacher_name: studentData.in_charge_teacher,
              in_charge_teacher: studentData.in_charge_teacher,
              lecture: studentData.lecture,
              acq_channel: studentData.acq_channel,
              referral_id: studentData.referral_id,
              is_blocked: studentData.is_blocked,
              created_at: studentData.created_at,
              updated_at: studentData.updated_at
            };
          }
          
          return null;
        } catch (error) {
          console.error('학생 정보 조회 중 오류:', error);
          return null;
        }
      };

      const latestStudentInfo = await fetchLatestStudentInfo(student.student_id);
      
      if (latestStudentInfo) {
        setSelectedStudentDetail(latestStudentInfo);
      } else {
        // 최신 정보를 못 가져온 경우 기존 데이터 사용
        console.warn('최신 학생 정보를 가져올 수 없어 기존 데이터를 사용합니다.');
      }
    } catch (error) {
      console.error('학생 상세 정보 조회 실패:', error);
      // 에러 발생시에도 기존 데이터 유지
    } finally {
      // 로딩 상태 종료
      setIsLoadingStudentDetail(false);
    }
  }, [getAccessToken, navigate, userType, userId]);

  // 선택된 학생 정보 업데이트 핸들러
  const handleSelectedStudentChange = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  // 학생 정보 수정 모달 열기
  const handleModalOpen = (mode, student = null, fieldName = null) => {
    // console.log('handleModalOpen called:', { mode, student, fieldName });
    setModalMode(mode);
    const studentData = {
      ...student,
      editField: fieldName,
      email: student?.student_email,
      phone_number: student?.mobile_number,
      parent_phone_number: student?.parent_mobile_number,
      student_id: student?.student_id  // student_id 추가
    };
    // console.log('Setting studentData:', studentData);
    setEditingStudent(studentData);
    setIsModalOpen(true);
  };

  // 학생 정보 수정
  const handleEditStudent = async (studentData) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: editingStudent.student_id,
          ...studentData
        })
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '학생 정보 수정에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        // 수정된 정보로 상태 업데이트
        const updatedStudent = {
          ...selectedStudentDetail,
          ...studentData
        };
        
        setSelectedStudentDetail(updatedStudent);
        
        setIsModalOpen(false);
        setShowDetailModal(true);
        return '학생 정보가 성공적으로 수정되었습니다.';
      }
    } catch (error) {
      throw error;
    }
  };

  // 학생 정보 수정 모달 닫기
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingStudent(null);
    setModalMode('add');
    
    if (modalMode === 'edit') {
      setShowDetailModal(true);
    }
  };

  const handleColumnSwitch = (direction) => {
    if (direction === 'left') {
      setVisibleHierarchyLevel(prev => Math.max(0, prev - 1));
    } else {
      setVisibleHierarchyLevel(prev => Math.min(3, prev + 1));
    }
  };

  // 결과맵 데이터 로딩 완료 시 스크롤 위치 초기화 (중유형이 보이도록)
  useEffect(() => {
    if (resultMapData) {
      // 데스크톱 뷰 스크롤 초기화 (중유형이 잘 보이도록 조정)
      if (resultMapTableRef.current) {
        const tableContainer = resultMapTableRef.current;
        const scrollLeft = Math.max(0, 180); // 대단원(120px) + 소단원 일부만 스크롤하여 중유형 강조
        setTimeout(() => {
          tableContainer.scrollLeft = scrollLeft;
        }, 100);
      }
      
      // 모바일 뷰 스크롤 초기화 (중유형이 잘 보이도록 조정)
      if (resultMapTableMobileRef.current) {
        const tableContainerMobile = resultMapTableMobileRef.current;
        const scrollLeft = Math.max(0, 150); // 모바일은 좀 더 작게 조정
        setTimeout(() => {
          tableContainerMobile.scrollLeft = scrollLeft;
        }, 100);
      }
    }
  }, [resultMapData]);









  // URL 기반에서는 상태 동기화 useEffect들이 필요 없음 - URL이 단일 진실 공급원

  // 테스트 변경 시 하이라이팅 상태 초기화
  useEffect(() => {
    dispatchHighlight({ type: 'RESET' });
  }, [selectedTest]);





  // 선택된 테스트의 결과 맵 생성 함수
  const getResultMap = (test) => {
    if (!test || !test.result) return new Map();
    // 새 API에서는 result가 {problem_id: boolean} 객체 형태
    return new Map(Object.entries(test.result).map(([problemId, result]) => [parseInt(problemId), result]));
  };

  // 문제의 정오답 결과 가져오기 함수
  const getProblemResult = (test, problemId) => {
    const resultMap = getResultMap(test);
    return resultMap.get(problemId);
  };

  // 문제 보기 모달 열기 함수
  const handleOpenProblemPreview = (problemIds, index, isCorrect = null) => {
    // problemIds가 배열이면 그대로 사용, 아니면 배열로 변환
    const ids = Array.isArray(problemIds) ? problemIds : [problemIds];
    
    // 문제 미리보기 상태 설정
    setPreviewState({
      problemId: ids,
      correct: isCorrect !== null ? [isCorrect] : [null],
      number: [index + 1]
    });
    // 문제 미리보기 모달 열기
    dispatchModal({ type: 'OPEN_MODAL', payload: 'problemPreview' });
  };

  // 시험지 미리보기 열기 함수
  const handlePrintExam = () => {
    dispatchModal({ type: 'OPEN_MODAL', payload: 'printPreview' });
  };





  const fetchTeachers = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/teacherstotal`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('강사 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setStudentModalState({ teachers: result.data || [] });
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const handleAllocateTeacher = async (studentId, teacherId) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            teacher_id: teacherId,
            student_id: studentId
          })
        }
      );

      if (!response.ok) throw new Error('강사 할당에 실패했습니다.');

      dispatchModal({ type: 'CLOSE_MODAL', payload: 'teacherAssign' });
      setStudentModalState({ selectedTeacherId: '' });
      setShowDetailModal(true);
      
      const message = teacherId ? '강사가 성공적으로 할당되었습니다.' : '강사 할당이 삭제되었습니다.';
      alert(message);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleTeacherModalClose = () => {
    dispatchModal({ type: 'CLOSE_MODAL', payload: 'teacherAssign' });
    setStudentModalState({ selectedTeacherId: '' });
    setShowDetailModal(true);
  };

  const handleResetPassword = async (studentId) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/resetpw`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ student_id: parseInt(studentId) })
      });

      if (!response.ok) {
        throw new Error('비밀번호 초기화에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        return result.data.temp_pw;
      } else {
        throw new Error(result.message || '비밀번호 초기화에 실패했습니다.');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedStudentDetail || studentModalState.deleteEmail !== selectedStudentDetail.student_email) {
      setStudentModalState({ deleteError: '이메일이 일치하지 않습니다.' });
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: parseInt(selectedStudentDetail.student_id)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '학생 삭제에 실패했습니다.');
      }

      alert('학생이 성공적으로 삭제되었습니다.');
      setShowDetailModal(false);
      dispatchModal({ type: 'CLOSE_MODAL', payload: 'deleteConfirm' });
      setStudentModalState({ deleteEmail: '' });
    } catch (err) {
      setStudentModalState({ deleteError: err.message });
    }
  };

  // 선택된 테스트의 난이도별 정답률 계산 함수
  const getLevelAchievementData = useCallback((test) => {
    if (!test) {
      return [1, 2, 3, 4, 5].map(level => ({
        level,
        currentAcc: 0,
        currentCorrect: 0,
        currentTotal: 0
      }));
    }

    const difficultyStats = [0, 0, 0, 0, 0]; // 각 레벨별 정답 수
    const difficultyTotals = [0, 0, 0, 0, 0]; // 각 레벨별 총 문제 수

    // 새 API의 by_step 데이터를 우선 사용
    if (test.by_step) {
      for (let level = 1; level <= 5; level++) {
        const stepData = test.by_step[level.toString()];
        if (stepData && stepData.length >= 4) {
          const [totalProblems, correctCount] = stepData;
          const diffIndex = level - 1;
          difficultyTotals[diffIndex] = totalProblems || 0;
          difficultyStats[diffIndex] = correctCount || 0;
        }
      }
    } else if (test.sequence || test.problems_list) {
      // 폴백: 기존 방식
      const resultMap = getResultMap(test);
      const problems = test.problems_list || test.sequence || [];

      problems.forEach(problem => {
        let difficulty, isCorrect;
        
        // problems_list 구조인 경우
        if (problem.problem_id) {
          difficulty = problem.step;
          isCorrect = resultMap.get(problem.problem_id);
        }
        // sequence 배열 구조인 경우
        else if (Array.isArray(problem)) {
          difficulty = problem[1]; // 난이도 (1~5)
          isCorrect = problem[6]; // 정답 여부
        }
        
        if (difficulty >= 1 && difficulty <= 5) {
          const index = difficulty - 1;
          difficultyTotals[index]++;
          if (isCorrect) {
            difficultyStats[index]++;
          }
        }
      });
    }

    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = difficultyStats[index];
      const total = difficultyTotals[index];
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      return {
        level,
        currentAcc: accuracy,
        currentCorrect: correct,
        currentTotal: total
      };
    });
  }, []);

  // 선택된 테스트의 레벨별 데이터를 가져오는 함수
  const getLevelData = (test) => {
    // test 객체에 level 관련 데이터가 있으면 반환
    // 데이터 구조: { lv1: 정답수, lv1_total: 전체수, lv2: 정답수, lv2_total: 전체수, ... }
    if (!test) return null;
    
    // reportData에서 가져오기 시도
    if (reportData && reportData.levelData) {
      return reportData.levelData;
    }
    
    // test 객체에서 직접 가져오기 시도
    const levelData = {};
    for (let i = 1; i <= 5; i++) {
      levelData[`lv${i}`] = test[`lv${i}`] || 0;
      levelData[`lv${i}_total`] = test[`lv${i}_total`] || 0;
    }
    
    // 만약 모든 값이 0이면 sequence/problems_list에서 계산
    const hasData = Object.values(levelData).some(val => val > 0);
    if (!hasData && (test.sequence || test.problems_list)) {
      const levelStats = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const levelTotals = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      
      if (test.sequence) {
        test.sequence.forEach(problem => {
          if (Array.isArray(problem)) {
            const difficulty = problem[1];
            const isCorrect = problem[6];
            if (difficulty >= 1 && difficulty <= 5) {
              levelTotals[difficulty]++;
              if (isCorrect) levelStats[difficulty]++;
            }
          }
        });
      } else if (test.problems_list) {
        test.problems_list.forEach(problem => {
          const difficulty = problem.step;
          const isCorrect = getProblemResult(test, problem.problem_id);
          if (difficulty >= 1 && difficulty <= 5) {
            levelTotals[difficulty]++;
            if (isCorrect) levelStats[difficulty]++;
          }
        });
      }
      
      for (let i = 1; i <= 5; i++) {
        levelData[`lv${i}`] = levelStats[i];
        levelData[`lv${i}_total`] = levelTotals[i];
      }
    }
    
    return levelData;
  };

  // 선택된 테스트의 레이더 차트 데이터 (정답률 기반 - 기존 코드 유지)
  const getLevelRadarData = (test) => {
    const levelData = getLevelAchievementData(test);
    
    return levelData.map(item => ({
      subject: `LV ${item.level}`,
      A: item.currentAcc,
      fullMark: 100,
    }));
  };

  // 선택된 테스트의 레이더 차트 데이터 (정답률 기반 성취도)
  const getLevelRadarDataByProblemCount = (test) => {
    if (!test) {
      return [1, 2, 3, 4, 5].map(level => ({
        subject: `LV ${level}`,
        A: 0,
        problemCount: 0,
        correctCount: 0,
        correctRate: 0,
        fullMark: 100,
      }));
    }

    const difficultyTotals = [0, 0, 0, 0, 0]; // 각 레벨별 총 문제 수
    const difficultyCorrects = [0, 0, 0, 0, 0]; // 각 레벨별 정답 수
    
    // 새 API의 by_step 데이터를 우선 사용
    if (test.by_step) {
      for (let level = 1; level <= 5; level++) {
        const stepData = test.by_step[level.toString()];
        if (stepData && stepData.length >= 4) {
          const [totalProblems, correctCount] = stepData;
          const diffIndex = level - 1;
          difficultyTotals[diffIndex] = totalProblems || 0;
          difficultyCorrects[diffIndex] = correctCount || 0;
        }
      }
    } else if (test.sequence || test.problems_list) {
      // 폴백: 기존 방식
      const problems = test.problems_list || test.sequence || [];
      const resultMap = getResultMap(test);

      problems.forEach(problem => {
        let difficulty, isCorrect;
        
        // problems_list 구조인 경우
        if (problem.problem_id) {
          difficulty = problem.step;
          isCorrect = resultMap.get(problem.problem_id);
        }
        // sequence 배열 구조인 경우
        else if (Array.isArray(problem)) {
          difficulty = problem[1]; // 난이도 (1~5)
          isCorrect = problem[6]; // 정답 여부
        }
        
        if (difficulty >= 1 && difficulty <= 5) {
          const index = difficulty - 1;
          difficultyTotals[index]++;
          if (isCorrect) {
            difficultyCorrects[index]++;
          }
        }
      });
    }

    // 최대 문제 수 찾기 (가중치 계산용)
    const maxProblems = Math.max(...difficultyTotals, 1);
    const totalProblems = difficultyTotals.reduce((sum, count) => sum + count, 0);

    return [1, 2, 3, 4, 5].map((level, index) => {
      const totalCount = difficultyTotals[index];
      const correctCount = difficultyCorrects[index];
      const correctRate = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
      
      // 성취도 계산: 정답률에 문제 수 가중치 적용
      let achievement = 0;
      if (totalCount > 0) {
        // 제곱근 스케일링: 문제 수가 많을수록 가중치가 높지만, 과도하게 높아지지 않음
        const weight = Math.sqrt(totalCount / maxProblems);
        achievement = Math.round(correctRate * weight);
        
        // 최소값 보장: 문제가 1개라도 있고 맞췄다면 최소 10% 표시
        if (correctCount > 0 && achievement < 10) {
          achievement = 10;
        }
      }
      
      return {
        subject: `Lv ${level}`,
        A: achievement,  // 정답률 × 문제수 가중치
        problemCount: totalCount,  // 실제 문제 수
        correctCount: correctCount,  // 맞춘 문제 수
        correctRate: correctRate,  // 정답률
        weight: totalCount > 0 ? Math.round(Math.sqrt(totalCount / maxProblems) * 100) : 0,  // 가중치 (%)
        fullMark: 100,
      };
    });
  };

  // 도넛 차트용 데이터 변환 함수
  const getLevelPieData = useCallback((test) => {
    const radarData = getLevelRadarDataByProblemCount(test);
    return radarData.map((item, index) => ({
      name: item.subject,
      value: item.A,
      problemCount: item.problemCount,
      correctCount: item.correctCount,
      correctRate: item.correctRate,
      weight: item.weight
    }));
  }, []);

  // 도넛 차트 active shape 렌더링 함수
  const renderActiveShape = useCallback((props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    
    return (
      <g>
        {/* 중앙 텍스트 */}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#333">
          <tspan x={cx} dy="-0.8em" fontSize={10} fontWeight="bold">{payload.name}</tspan>
          <tspan x={cx} dy="1.4em" fontSize={8} fill="#666">성취도: {value}%</tspan>
          <tspan x={cx} dy="1.4em" fontSize={8} fill="#999">
            {payload.correctCount}/{payload.problemCount}문제
          </tspan>
          <tspan x={cx} dy="1.2em" fontSize={10} fill="#999">
            {/* 정답률 {payload.correctRate}% */}
          </tspan>
        </text>
        
        {/* 기본 섹터 */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          cornerRadius={8}
          isAnimationActive={false}
        />
        
        {/* 하이라이트 섹터 */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle + 8}
          endAngle={endAngle - 8}
          innerRadius={outerRadius + 3}
          outerRadius={outerRadius + 6}
          fill={fill}
          cornerRadius={8}
          isAnimationActive={false}
        />
      </g>
    );
  }, []);



  // 리포트 데이터 가져오기 함수
  const fetchReportData = async (stampId, examId) => {
    try {
      setIsReportLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        // console.log(`No report found for exam ${examId}`);
        return null;
      }

      const reportData = await response.json();
      // console.log('Report data for exam', examId, ':', reportData);
      return reportData.data;
    } catch (error) {
      console.error('Error fetching report data:', error);
      return null;
    } finally {
      setIsReportLoading(false);
    }
  };

  // 특정 stamp의 가장 최근 리포트 데이터 로드
  const loadReportDataForStamp = async (targetStampId) => {
    if (!testHistory || testHistory.length === 0 || !targetStampId) {
      setReportData(null);
      setCurrentReportStampId(null);
      return;
    }

    // 해당 stamp의 리포트가 있는 테스트들을 TID 순으로 정렬 (최신순)
    const testsWithReports = testHistory
      .filter(test => test.report_status >= 200 && test.stamp_id === targetStampId)
      .sort((a, b) => parseInt(b.exam_id) - parseInt(a.exam_id));

    if (testsWithReports.length === 0) {
      // 해당 stamp에 리포트가 없는 경우
      if (currentReportStampId !== targetStampId) {
        setReportData(null);
        setCurrentReportStampId(targetStampId);
      }
      return;
    }

    // TID가 큰 순서부터 차례대로 호출하여 가장 많은 레이팅 이력을 찾기
    for (const test of testsWithReports) {
      const reportData = await fetchReportData(test.stamp_id, test.exam_id);
      if (reportData && reportData.exams && reportData.exams.length > 0) {
        setReportData(reportData);
        setCurrentReportStampId(targetStampId);
        break; // 첫 번째로 찾은 리포트 사용 (TID가 가장 큰 것)
      }
    }
  };

  // 전체 테스트 이력에서 가장 최근 리포트 데이터 로드 (초기 로드용)
  const loadLatestReportData = async () => {
    if (!testHistory || testHistory.length === 0) {
      setReportData(null);
      setCurrentReportStampId(null);
      return;
    }

    // 리포트가 있는 테스트들을 TID 순으로 정렬 (최신순)
    const testsWithReports = testHistory
      .filter(test => test.report_status >= 200)
      .sort((a, b) => parseInt(b.exam_id) - parseInt(a.exam_id));

    if (testsWithReports.length === 0) {
      setReportData(null);
      setCurrentReportStampId(null);
      return;
    }

    // TID가 큰 순서부터 차례대로 호출하여 가장 많은 레이팅 이력을 찾기
    for (const test of testsWithReports) {
      const reportData = await fetchReportData(test.stamp_id, test.exam_id);
      if (reportData && reportData.exams && reportData.exams.length > 0) {
        setReportData(reportData);
        setCurrentReportStampId(test.stamp_id);
        break; // 첫 번째로 찾은 리포트 사용 (TID가 가장 큰 것)
      }
    }
  };

  // 중복된 useEffect 제거됨 (위에서 통합 처리)

  // 테스트 이력이 로드된 후 리포트 데이터 로드
  useEffect(() => {
    if (testHistory.length > 0) {
      loadLatestReportData();
    }
  }, [testHistory]);

  // 선택된 테스트의 stamp가 변경될 때 해당 stamp의 리포트 데이터 로드
  useEffect(() => {
    if (selectedTest && selectedTest.stamp_id && currentReportStampId !== selectedTest.stamp_id) {
      loadReportDataForStamp(selectedTest.stamp_id);
    }
  }, [selectedTest?.stamp_id, currentReportStampId]);

  // 차트 데이터 캐싱을 위한 메모이제이션
  const chartDataCache = useRef({});
  
  // 학생의 폼레이팅 변화 추이 차트 데이터 (stamp별 구분)
  const getExamChartData = useCallback((student) => {
    // 캐시 키 생성 (뷰 모드별로 분리하여 모바일/PC 전환시 올바른 데이터 보장)
    const cacheKey = `${isMobileView ? 'mobile' : 'desktop'}_${selectedTest?.stamp_id || 'all'}_${testHistory?.length || 0}_${reportData?.exams?.length || 0}`;
    
    // 캐시된 데이터가 있으면 반환
    if (chartDataCache.current[cacheKey]) {
      return chartDataCache.current[cacheKey];
    }
    // 테스트 이력이 없으면 빈 배열 반환
    if (!testHistory || testHistory.length === 0) return [];

    // 선택된 테스트가 있으면 해당 stamp_id의 데이터만 필터링
    const stampId = selectedTest?.stamp_id;
    
    // 테스트 이력을 필터링하고 시간순으로 정렬 (과거 → 현재)
    const sortedTests = [...testHistory]
      .filter(test => {
        // 확정된 테스트만
        if (test.exam_status !== 999) return false;
        // stamp_id 필터링 (selectedTest가 있을 경우)
        if (stampId && test.stamp_id !== stampId) return false;
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.created_at || a.exam_created_at || a.updated_at);
        const dateB = new Date(b.created_at || b.exam_created_at || b.updated_at);
        return dateA - dateB;
      });

    if (sortedTests.length === 0) return [];

    // 동일 exam_round 중복 제거 (최신 updated_at을 가진 시험만 유지)
    const uniqueTests = sortedTests.reduce((acc, exam) => {
      const existingIndex = acc.findIndex(item => item.exam_round === exam.exam_round);
      
      if (existingIndex === -1) {
        // 해당 exam_round가 없으면 추가
        acc.push(exam);
      } else {
        // 해당 exam_round가 이미 있으면 더 최신 것으로 대체
        const existing = acc[existingIndex];
        const examDate = new Date(exam.updated_at || exam.created_at || 0);
        const existingDate = new Date(existing.updated_at || existing.created_at || 0);
        
        if (examDate > existingDate) {
          acc[existingIndex] = exam; // 더 최신 데이터로 대체
        }
      }
      
      return acc;
    }, []);

    // 각 테스트에 대해 차트 데이터 생성 (폼레이팅 데이터가 있는 것만)
    const chartData = [];
    let chartIndex = 1;
    
    uniqueTests.forEach((exam, index) => {
      const totalQuestions = (exam.correct_count || 0) + (exam.wrong_count || 0);
      const accuracy = totalQuestions > 0 ? Math.round(((exam.correct_count || 0) / totalQuestions) * 100) : 0;
      
      // 난이도별 정답 분포 계산
      const difficultyDistribution = [0, 0, 0, 0, 0]; // Level 1~5
      const difficultyTotals = [0, 0, 0, 0, 0]; // 각 난이도별 총 문제 수
      
      // 새 API의 by_step 데이터를 활용하여 난이도별 정답 분포 계산
      if (exam.by_step) {
        for (let level = 1; level <= 5; level++) {
          const stepData = exam.by_step[level.toString()];
          if (stepData && stepData.length >= 4) {
            const [totalProblems, correctCount] = stepData;
            const diffIndex = level - 1;
            difficultyTotals[diffIndex] = totalProblems || 0;
            difficultyDistribution[diffIndex] = correctCount || 0;
          }
        }
      } else if (exam.problems_list && exam.problems_list.length > 0) {
        // 폴백: 기존 방식 (problems_list가 있는 경우)
        const resultMap = new Map(Object.entries(exam.result || {}).map(([problemId, result]) => [parseInt(problemId), result]));
        
        exam.problems_list.forEach(problem => {
          const difficulty = problem.step;
          if (difficulty >= 1 && difficulty <= 5) {
            const diffIndex = difficulty - 1;
            difficultyTotals[diffIndex]++;
            const isCorrect = resultMap.get(problem.problem_id);
            if (isCorrect) {
              difficultyDistribution[diffIndex]++;
            }
          }
        });
      } else if (exam.sequence && Array.isArray(exam.sequence)) {
        exam.sequence.forEach(problem => {
          if (Array.isArray(problem)) {
            const difficulty = problem[1]; // 난이도
            const isCorrect = problem[6]; // 정답 여부
            if (difficulty >= 1 && difficulty <= 5) {
              const diffIndex = difficulty - 1;
              difficultyTotals[diffIndex]++;
              if (isCorrect) {
                difficultyDistribution[diffIndex]++;
              }
            }
          }
        });
      }
      
      const totalCorrect = difficultyDistribution.reduce((sum, count) => sum + count, 0);
      
      // 각 난이도별 비율 계산 (전체 정답률 대비 각 난이도의 기여도)
      const difficultyRatios = difficultyDistribution.map((count, idx) => {
        const total = difficultyTotals[idx];
        if (total === 0) return 0;
        // 해당 난이도의 정답 비율을 전체 정답률에 맞춰 조정
        return (count / totalQuestions) * 100;
      });
      
      // 폼레이팅 데이터 확인
      let rating = null;
      let delta = 0;
      
      // 리포트 데이터가 있으면 해당 exam_id의 rating을 찾아서 사용
      if (reportData && reportData.exams && reportData.exams.length > 0) {
        const reportExam = reportData.exams.find(r => r.exam_id === exam.exam_id);
        if (reportExam && reportExam.rating) {
          rating = reportExam.rating;
          delta = reportExam.delta || 0;
        }
      }
      
      // 리포트 데이터가 없으면 기존 값 확인
      if (rating === null && exam.rating && exam.rating !== 1000) {
        rating = exam.rating;
        delta = exam.delta || 0;
      }
      
      // 폼레이팅 데이터가 없어도 차트에 포함 (정답률 데이터 표시용)
      // if (rating === null || rating === 1000) {
      //   return;
      // }
      
      // 이전 점수와의 차이 계산
      let prevRating = null;
      let ratingChange = 0;
      let ratingChangePercent = 0;
      
      if (chartData.length > 0) {
        prevRating = chartData[chartData.length - 1].rating;
        ratingChange = rating - prevRating;
        ratingChangePercent = ((rating - prevRating) / prevRating * 100).toFixed(2);
      }
      
      chartData.push({
        x: chartIndex, // x축: 회차
        rating: rating || null, // 폼레이팅 (없는 경우 null)
        prevRating: prevRating, // 이전 폼레이팅
        ratingChange: ratingChange, // 폼레이팅 변화량 (절대값)
        ratingChangePercent: ratingChangePercent, // 폼레이팅 변화율 (%)
        accuracy: accuracy, // 정답률
        exam_id: exam.exam_id,
        exam_round: exam.exam_round || chartIndex, // 실제 회차 정보
        delta: delta, // 폼레이팅 변화량
        difficulty: exam.difficulty || 2.5, // 전체 시험 난이도
        // 스택형 막대그래프를 위한 난이도별 데이터
        level1: difficultyRatios[0], // Level 1 기여도
        level2: difficultyRatios[1], // Level 2 기여도
        level3: difficultyRatios[2], // Level 3 기여도
        level4: difficultyRatios[3], // Level 4 기여도
        level5: difficultyRatios[4], // Level 5 기여도
        correctByLevel: difficultyDistribution, // 난이도별 맞춘 문제 수
        totalByLevel: difficultyTotals, // 난이도별 총 문제 수
        totalCorrect: totalCorrect, // 총 맞춘 문제 수
        totalQuestions: totalQuestions, // 총 문제 수
        testDate: exam.created_at || exam.exam_created_at || exam.updated_at, // 시험 날짜
        exam_type: exam.exam_type || 'unknown',
        tree_name: exam.tree_name || '',
        stamp_id: exam.stamp_id,
        hasRating: rating !== null // 폼레이팅 데이터 유무
      });
      
      // chartIndex를 별도로 증가
      chartIndex++;
    });
    
    // 결과를 캐시에 저장
    chartDataCache.current[cacheKey] = chartData;
    
    return chartData;
  }, [testHistory, selectedTest?.stamp_id, reportData, isMobileView]);

  // Y축 도메인 계산 함수 (폼레이팅 범위에 따라 동적 계산)
  const getYAxisDomain = useCallback((student) => {
    const chartData = getExamChartData(student);
    if (chartData.length === 0) return [1000, 1200]; // 기본 범위
    
    const ratingValues = chartData.map(item => item.rating).filter(rating => rating !== null);
    
    // 폼레이팅 데이터가 없는 경우 기본 범위 사용
    if (ratingValues.length === 0) {
      return [1000, 1200];
    }
    
    const minRating = Math.min(...ratingValues);
    const maxRating = Math.max(...ratingValues);
    
    // 데이터 포인트가 1개일 때와 여러 개일 때 다르게 처리
    if (ratingValues.length === 1) {
      const rating = ratingValues[0];
      const margin = 100; // 고정 여유 범위
      return [Math.max(1000, rating - margin), Math.min(2500, rating + margin)];
    }
    
    // 다중 데이터 포인트인 경우 (폼레이팅 이력이 있는 경우)
    // Y축 범위 설정 (약간의 여유 추가)
    const range = maxRating - minRating;
    const margin = Math.max(50, range * 0.1); // 최소 50, 범위의 10% 중 큰 값
    return [
      Math.floor((minRating - margin) / 50) * 50, // 50 단위로 내림
      Math.ceil((maxRating + margin) / 50) * 50   // 50 단위로 올림
    ];
  }, [getExamChartData]);

  // 도넛 차트 데이터 메모이제이션
  const pieChartData = useMemo(() => {
    if (!selectedTest) return [];
    return getLevelPieData(selectedTest);
  }, [getLevelPieData, selectedTest]);


  // 결과맵 데이터를 문제 리스트와 동일한 구조로 변환
  const resultMapList = useMemo(() => {
    if (!resultMapData || !resultMapData.field_info) {
      return [];
    }

    const d4Data = resultMapData.field_info.d4_y_axis || [];
    
    return d4Data.map((item, index) => {
      const path = item[2]; // 경로 정보
      const status = item[3]; // 상태 배열
      const name = item[1]; // 이름
      
      // 경로에서 부모 정보 추출
      const pathParts = path.match(/\d+/g) || [];
      
      // 부모 정보 찾기
      const d1Item = resultMapData.field_info.d1_y_axis?.find(d1 => 
        d1[2].includes(`${pathParts[0]}/${pathParts[1]}`)
      );
      const d2Item = resultMapData.field_info.d2_y_axis?.find(d2 => 
        d2[2].includes(`${pathParts[0]}/${pathParts[1]}/${pathParts[2]}`)
      );
      const d3Item = resultMapData.field_info.d3_y_axis?.find(d3 => 
        d3[2].includes(`${pathParts[0]}/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}`)
      );

      return {
        id: index, // 문제 리스트의 index와 유사한 고유 ID
        d1Name: d1Item ? decodeHtml(d1Item[1]) : '',
        d2Name: d2Item ? decodeHtml(d2Item[1]) : '',
        d3Name: d3Item ? decodeHtml(d3Item[1]) : '',
        d4Name: decodeHtml(name),
        status: status,
        address: pathParts.join('/'),
        // 문제 리스트와 유사한 추가 속성들
        displayName: decodeHtml(name), // 표시될 이름
        levels: status // 난이도별 상태
      };
    });
  }, [resultMapData]);





  // 초기화 상태 확인
  if (!isInitialized) {
    return (
      <BypassLayout loading={true}>
        <LoadingSpinner message="초기화 중..." />
      </BypassLayout>
    );
  }

  // 로딩 조건 개선 - 무한 루프 방지
  const isPageLoading = () => {
    // URL 직접 접근 시 학생 정보 로딩 중
    if (studentId && !selectedStudent && isLoadingDirectAccess) {
      return true;
    }
    
    // testId가 있을 때의 로딩 조건 - 더 엄격하게 제한
    if (testId && selectedStudent) {
      // 학생은 있지만 테스트 이력이 로딩 중이거나 아직 없는 경우
      if (isTestHistoryLoading) return true;
      
      // 테스트 이력은 있지만 해당 testId의 테스트를 찾지 못한 경우
      // 하지만 여전히 로딩 중이 아니라면 에러 상태로 처리
      if (testHistory.length > 0 && !selectedTest && !isTestHistoryLoading) {
        return false; // 에러 상태로 넘어가서 "테스트를 찾을 수 없음" 메시지 표시
      }
      
      return false; // 로딩 완료
    }
    
    return false;
  };

  // 에러 상태 확인
  const getErrorState = () => {
    // URL 직접 접근 시 학생을 찾지 못한 경우
    if (studentId && !selectedStudent && !isLoadingDirectAccess && directAccessError) {
      return { 
        title: '학생을 찾을 수 없습니다', 
        message: directAccessError,
        action: () => navigate('/learning-status')
      };
    }
    
    // testId가 있지만 해당 테스트를 찾지 못한 경우
    // URL 모드에서는 더 이상 로드할 테스트가 없을 때만 에러 표시
    if (testId && selectedStudent && testHistory.length > 0 && !selectedTest && !isTestHistoryLoading) {
      // URL 모드인지 확인
      if (isUrlMode) {
        // URL 모드에서는 더 이상 로드할 테스트가 없을 때만 에러
        if (!hasMoreTests) {
          return { 
            title: '테스트를 찾을 수 없습니다', 
            message: `TID ${testId}에 해당하는 테스트를 찾을 수 없습니다.`,
            action: () => navigate(`/learning-status/student/${studentId}`)
          };
        }
      } else {
        // 일반 모드에서는 기존 로직 유지
        const maxExamId = Math.max(...testHistory.map(test => test.exam_id));
        const minExamId = Math.min(...testHistory.map(test => test.exam_id));
        const testIdNum = parseInt(testId);
        
        if ((testIdNum <= maxExamId && testIdNum >= minExamId) || !hasMoreTests) {
          return { 
            title: '테스트를 찾을 수 없습니다', 
            message: `TID ${testId}에 해당하는 테스트를 찾을 수 없습니다.`,
            action: () => navigate(`/learning-status/student/${studentId}`)
          };
        }
      }
    }
    
    return null;
  };

  const pageLoading = isPageLoading();
  const errorState = getErrorState();

  return (
    <>
      {/* 플로팅 버튼들 - BypassLayout 밖에서 렌더링 */}
      {/* 모바일 플로팅 이전/다음 테스트 이동 버튼 */}
      {isMobileView && currentMobilePanel === 'area3' && selectedTest && (() => {
        // 동일한 stamp_id를 가진 테스트들을 시간순으로 정렬
        const sameStampTests = testHistory
          .filter(test => test.stamp_id === selectedTest.stamp_id)
          .sort((a, b) => {
            const dateA = new Date(a.created_at || a.exam_created_at || a.updated_at);
            const dateB = new Date(b.created_at || b.exam_created_at || b.updated_at);
            return dateA - dateB;
          });
        
        const currentIndex = sameStampTests.findIndex(test => test.exam_id === selectedTest.exam_id);
        const prevTest = currentIndex > 0 ? sameStampTests[currentIndex - 1] : null;
        const nextTest = currentIndex < sameStampTests.length - 1 ? sameStampTests[currentIndex + 1] : null;
        
        if (!prevTest && !nextTest) return null;
        
        return (
          <>
            <FloatingButton
              variant="prev"
              title={prevTest ? `이전: TID ${prevTest.exam_id}` : '이전 테스트 없음'}
              onClick={(e) => {
                e.stopPropagation();
                if (prevTest) enhancedNavigateToTest(selectedStudent, prevTest);
              }}
              disabled={!prevTest}
              usePortal={true}
            />
            <FloatingButton
              variant="next"
              title={nextTest ? `다음: TID ${nextTest.exam_id}` : '다음 테스트 없음'}
              onClick={(e) => {
                e.stopPropagation();
                if (nextTest) enhancedNavigateToTest(selectedStudent, nextTest);
              }}
              disabled={!nextTest}
              usePortal={true}
            />
          </>
        );
      })()}

      {/* 데스크톱 플로팅 이전/다음 테스트 이동 버튼 - 데스크톱에서 비활성화됨 */}
      {/* TODO: 데스크톱에서 플로팅 버튼이 필요할 때 주석 해제 */}

      {/* 에러 상태 처리 */}
      {errorState ? (
        <BypassLayout>
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <h2 className="mb-4 text-xl font-semibold text-gray-800">{errorState.title}</h2>
              <p className="mb-6 text-gray-600">{errorState.message}</p>
              <Button 
                variant="primary" 
                onClick={errorState.action}
              >
                돌아가기
              </Button>
            </div>
          </div>
        </BypassLayout>
      ) : (
        <BypassLayout
          loading={pageLoading}
          extendViewportToNav={isMobileView && currentMobilePanel === 'area3'}
          renderContent={({ isMobileView, getMobileContainerHeight, getMobilePanelHeight, getSafeAreaStyle, getBottomPaddingWithNav }) => (
          <>
            {/* 모바일 뷰 */}
            {isMobileView ? (
              <>

                              {/* 패널 표시 (세 영역 중 하나만 표시) */}
                {currentMobilePanel === 'area1' && (
                  <StudentSection
                    userType={userType}
                    navigateToStudent={navigateToStudent}
                    navigateToStudentList={navigateToStudentList}
                    onStudentDetailClick={handleStudentDetailClick}
                    onSelectedStudentChange={handleSelectedStudentChange}
                    isMobile={true}
                    isStudentSelected={isStudentSelected}
                    selectedStudent={selectedStudent}
                    studentListRef={studentListRef}
                    // 테스트 관련 props
                    testHistory={testHistory}
                    selectedTest={selectedTest}
                    isTestHistoryLoading={isTestHistoryLoading}
                    hasMoreTests={hasMoreTests}
                    onTestSelect={(test) => enhancedNavigateToTest(selectedStudent, test)}
                    testHistoryRef={testHistoryRef}
                    setCurrentMobilePanel={setCurrentMobilePanel}
                    loadMoreTests={loadMoreTests}
                  />
                )}
 



              {currentMobilePanel === 'area3' && (
                <div className="flex flex-col gap-4 bg-gray-100" style={{ 
                  padding: '1rem',
                  ...getBottomPaddingWithNav(),
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  {selectedTest ? (
                    <>
                      {/* 선택된 학생 카드 - area1 디자인 */}
                      <div 
                        className="relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-shadow hover:shadow-xl border-transparent ring-2 ring-primary"
                        onClick={navigateToStudentList}
                        style={{ order: 0 }}
                        >
                          <div className="grid grid-cols-[6fr_4fr_44px] items-center">
                            {/* 좌측 영역 - 학생 정보 */}
                            <div className="flex min-w-0 items-center gap-2 pr-2">
                              <div 
                                className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                                style={{ width: '32px', height: '32px' }}
                                title={`학생 ID: ${selectedStudent.student_id}`}
                              >
                                {selectedStudent.student_id}
                              </div>
                              
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <h4 className="truncate text-sm font-medium">{selectedStudent.student_name}</h4>
                                <p className="truncate text-xs text-gray-600">{selectedStudent.school_name} · {selectedStudent.school_grade}학년</p>
                                {selectedStudent.student_email && (
                                  <p className="truncate text-xs text-gray-500">{selectedStudent.student_email}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* 중앙 영역 - 담당 강사 & 강의반 정보 */}
                            <div className="flex items-center">
                              <div className="flex flex-1 flex-col justify-center text-right">
                                {((selectedStudent.teacher_name || selectedStudent.in_charge_teacher) || selectedStudent.lecture) ? (
                                  <>
                                    {(selectedStudent.teacher_name || selectedStudent.in_charge_teacher) && (
                                      <div className="flex items-center justify-end text-xs">
                                        <span className="shrink-0 text-gray-500">담당:</span>
                                        <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                                          {selectedStudent.teacher_name || selectedStudent.in_charge_teacher}
                                        </span>
                                      </div>
                                    )}
                                    {selectedStudent.lecture && (
                                      <div className="mt-0.5 flex items-center justify-end text-xs">
                                        <span className="shrink-0 text-gray-500">강의:</span>
                                        <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                                          {selectedStudent.lecture}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-xs text-gray-400">-</div>
                                )}
                              </div>
                            </div>
                            
                            {/* 우측 영역 - 빈 공간 (Edit 버튼 자리) */}
                            <div></div>
                          </div>
                          <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center text-xs text-primary">
                            <Undo2 size={16} />
                          </div>
                        </div>

                      {/* 선택된 테스트 카드 - TestHistoryItem 사용 */}
                      <div className="relative">
                        <TestHistoryItem 
                          test={selectedTest}
                          onClick={() => navigate(`/learning-status/student/${studentId}`)}
                          isSelected={true}
                          index={0}
                        />
                        <div className="absolute inset-x-0 top-1/4 flex -translate-y-1/2 items-center justify-center text-xs text-primary">
                          <Undo2 size={16} />
                        </div>
                      </div>

                                        {/* 난이도별 분석 카드 - 모바일용 */}
                                    <div className="rounded-3xl bg-white shadow-lg" style={{ order: 2 }}>
                                        <div className="px-4 pt-4">
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-[14px] font-semibold">난이도별 분석</h3>
                        <div className="rounded text-[9px] font-medium text-gray-500">
                          TID {selectedTest.exam_id} | {selectedTest.tree_name}
                        </div>
                      </div>
                    </div>
                    <div className="px-[14.4px] pb-[14.4px]">
                      {/* 차트 영역 - 1:1 비율 */}
                      <div className="grid grid-cols-2 gap-4">
                                                    {/* 난이도별 정답률 (1/2 영역) */}
                            <DifficultyAccuracyChartSection
                              selectedTest={selectedTest}
                              getLevelAchievementData={getLevelAchievementData}
                              getYAxisLabelColor={getYAxisLabelColor}
                            />

                        {/* 난이도별 성취도 (1/2 영역) */}
                        <DifficultyAchievementChartSection
                          selectedTest={selectedTest}
                          getLevelPieData={getLevelPieData}
                          renderActiveShape={renderActiveShape}
                          activePieIndex={activePieIndex}
                          setActivePieIndex={setActivePieIndex}
                        />
                      </div>
                    </div>
                  </div>

                                            {/* 결과맵 요약 */}
                      {resultMapData && (
                        <div className="rounded-3xl bg-white p-4 shadow-lg" style={{ order: 3 }}>
                          <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-[14px] font-bold text-gray-800">결과맵 요약</h3>
                            {selectedTest?.exam_status === 999 && (
                              <Button
                                onClick={() => dispatchModal({ type: 'OPEN_MODAL', payload: 'report' })}
                                className={`${getReportStatusInfo(selectedTest.report_status || 0).color} ${getReportStatusInfo(selectedTest.report_status || 0).textColor} hover:opacity-80`}
                                size="xs"
                                rounded="3xl"
                                icon={<FileText size={12} />}
                                title={getReportStatusInfo(selectedTest.report_status || 0).tooltip}
                              >
                                리포트
                              </Button>
                            )}
                          </div>

                          <div className="relative">
                            <div className="problem-list-table max-h-80 overflow-y-auto rounded border border-gray-200" ref={resultMapTableMobileRef}>
                              <table className="w-full table-fixed divide-y divide-gray-200">
                                <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                                  <tr>
                                    <th className="bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">
                                      <div className="flex items-center justify-between">
                                        <span>
                                          {['대단원', '소단원', '대유형', '중유형'][visibleHierarchyLevel]}
                                        </span>
                                        <div className="ml-2 flex gap-1">
                                          <button
                                            onClick={() => handleColumnSwitch('left')}
                                            className="rounded p-0.5 hover:bg-gray-200"
                                            title="이전 유형"
                                          >
                                            <ChevronLeft size={12} />
                                          </button>
                                          <button
                                            onClick={() => handleColumnSwitch('right')}
                                            className="rounded p-0.5 hover:bg-gray-200"
                                            title="다음 유형"
                                          >
                                            <ChevronRight size={12} />
                                          </button>
                                        </div>
                                      </div>
                                    </th>
                                    <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 1 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>1</th>
                                    <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 2 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>2</th>
                                    <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 3 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>3</th>
                                    <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 4 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>4</th>
                                    <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 5 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>5</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {(resultMapList || []).map((resultMapItem, index) => {
                                    const currentColumnKey = ['d1Name', 'd2Name', 'd3Name', 'd4Name'][visibleHierarchyLevel];
                                    
                                    if (!resultMapItem) return null;
                                    
                                    return (
                                      <tr 
                                        key={index}
                                        ref={el => rowRefs.current[resultMapItem.address] = el}
                                        className={`${index % 2 === 0 ? 'bg-gray-50' : ''} ${(resultMapItem.address === highlightState.address && highlightState.address) ? 'bg-green-200' : ''}`}
                                      >
                                        <td 
                                          className="max-w-0 truncate p-2 text-xs text-gray-900"
                                          title={resultMapItem[currentColumnKey]?.replace(/<[^>]*>/g, '') || ''}
                                        >
                                          <div className="truncate" dangerouslySetInnerHTML={{ __html: resultMapItem[currentColumnKey] || '' }} />
                                        </td>
                                        {[0, 1, 2, 3, 4].map(levelIndex => (
                                          <td key={levelIndex} className="w-10 py-2 text-center">
                                            <div className="flex justify-center">
                                              <div 
                                                className={`size-3 rounded ${getStatusColor(resultMapItem.levels?.[levelIndex] || 0)} ${(resultMapItem.levels?.[levelIndex] || 0) === 0 ? 'border border-gray-300' : ''} ${resultMapItem.address === highlightState.address && highlightState.difficulty === (levelIndex + 1) && highlightState.address ? 'ring-4 ring-blue-600 ring-offset-1' : ''}`}
                                                title={`Level ${levelIndex + 1}: ${resultMapItem.levels?.[levelIndex] || 0}`}
                                              >
                                                {(resultMapItem.levels?.[levelIndex] || 0) === 0 && (
                                                  <span className="text-xs leading-3 text-gray-500">×</span>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 문제 리스트 */}
                      <div className="rounded-3xl bg-white p-4 shadow-lg" style={{ order: 4 }}>
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="text-[14px] font-bold text-gray-800">문제 리스트</h3>
                          <span className="text-[11px] font-bold">
                            <span className="text-primary">{selectedTest?.correct_count || 0}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-danger">{selectedTest?.wrong_count || 0}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-black">{(selectedTest?.correct_count || 0) + (selectedTest?.wrong_count || 0)}</span>
                          </span>
                          <Button
                            onClick={handlePrintExam}
                            variant="secondary"
                            className="!bg-gray-400 text-white hover:opacity-80"
                            size="xs"
                            rounded="3xl"
                            icon={<FileText size={12} />}
                            title="시험지 미리보기"
                          >
                            시험지
                          </Button>
                        </div>
                        
                        {/* 카드 형태의 문제 미리보기 컴포넌트 */}
                        {selectedTest && (selectedTest.problems_list || selectedTest.sequence) ? (
                          <ProblemPreviewCards
                            problems={selectedTest.problems_list || selectedTest.sequence || []}
                            selectedTest={selectedTest}
                            onViewProblem={handleOpenProblemPreview}
                            highlightedIndex={highlightState.problemIndex}
                            onHighlight={(index, problemData) => {
                              // 하이라이트 상태 한 번에 업데이트
                              dispatchHighlight({
                                type: 'SET_ALL',
                                payload: {
                                  problemIndex: index,
                                  difficulty: problemData.difficulty ? Number(problemData.difficulty) : null,
                                  address: problemData.address || null
                                }
                              });
                            }}
                            getProblemResult={getProblemResult}
                          />
                        ) : (
                          <div className="flex items-center justify-center py-8">
                            <p className="text-xs text-gray-500">문제 정보가 없습니다.</p>
                          </div>
                        )}
                        
                        {/* 기존 테이블 코드 (주석 처리 - 복구 가능) */}
                        {/* {selectedTest && (selectedTest.problems_list || selectedTest.sequence) ? (
                          <div className="problem-list-table max-h-80 overflow-y-auto rounded border border-gray-200">
                            <table className="w-full table-fixed divide-y divide-gray-200">
                              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                                <tr>
                                  <th className="w-12 bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">번호</th>
                                  <th className="w-16 bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">문제ID</th>
                                  <th className="w-10 bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">난이도</th>
                                  <th className="w-12 bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">결과</th>
                                  <th className="w-12 bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">보기</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {(selectedTest.problems_list || selectedTest.sequence || []).map((problem, index) => {
                                  let problemId, isCorrect, difficulty, address;
                                  
                                  // problems_list 구조인 경우
                                  if (problem.problem_id) {
                                    problemId = problem.problem_id;
                                    isCorrect = getProblemResult(selectedTest, problemId);
                                    difficulty = problem.step;
                                    address = problem.address;
                                  }
                                  // sequence 배열 구조인 경우
                                  else if (Array.isArray(problem)) {
                                    problemId = problem[0];
                                    isCorrect = problem[6];
                                    difficulty = problem[1];
                                    const problemFromList = selectedTest.problems_list?.find(p => p.problem_id === problemId);
                                    address = problemFromList?.address;
                                  }
                                  
                                  if (!problemId) return null;
                                  
                                  return (
                                    <tr 
                                      key={index} 
                                      className={`${index % 2 === 0 ? 'bg-gray-50' : ''} cursor-pointer hover:bg-green-100 ${highlightState.problemIndex === index ? 'bg-green-200' : ''}`}
                                      onClick={() => {
                                        // 하이라이트 상태 한 번에 업데이트
                                        dispatchHighlight({
                                          type: 'SET_ALL',
                                          payload: {
                                            problemIndex: index,
                                            difficulty: difficulty ? Number(difficulty) : null,
                                            address: address || null
                                          }
                                        });
                                      }}
                                    >
                                      <td className="p-2 text-xs">{index + 1}</td>
                                      <td className="p-2 text-xs">{problemId}</td>
                                      <td className="p-2 text-xs">{difficulty || '-'}</td>
                                      <td className="p-2 text-xs">
                                        {isCorrect !== undefined && isCorrect !== null ? (
                                          isCorrect ? (
                                            <span className="font-bold text-blue-500">O</span>
                                          ) : (
                                            <span className="font-bold text-red-500">X</span>
                                          )
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="p-2 text-xs">
                                        <IconButton 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenProblemPreview([problemId], index, isCorrect);
                                          }}
                                          variant="light"
                                          size="xs"
                                          rounded={false}
                                          title="문제 보기"
                                        >
                                          <Eye size={10} />
                                        </IconButton>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center py-8">
                            <p className="text-xs text-gray-500">문제 정보가 없습니다.</p>
                          </div>
                        )} */}
                      </div>

                      {/* 폼레이팅 변화 추이 - DOM 순서는 마지막이지만 order로 첫 번째에 위치 */}
                      <div style={{ order: 1 }}>
                        <FormRatingChartSection
                          selectedStudent={selectedStudent}
                          selectedTest={selectedTest}
                          getExamChartData={getExamChartData}
                          getYAxisDomain={getYAxisDomain}
                          reportData={reportData}
                          onTestSelect={(test) => enhancedNavigateToTest(selectedStudent, test)}
                          testHistory={testHistory}
                        />
                      </div>
                    </>
                  ) : (
                    // testId가 없는 경우 - 일반적인 선택 안내
                    <div className="flex h-full items-center justify-center rounded-3xl bg-white p-4 shadow-lg">
                      <p className="text-sm text-gray-500">중간 패널에서 테스트를 선택하세요</p>
                    </div>
                  )}
                </div>
              )}
              </>
            ) : (
            // 데스크톱 뷰
            <div className="learning-status-desktop h-[calc(100vh-7rem)]">
              {/* 좌측 영역 - 학생 목록/선택된 학생 + 테스트 목록 */}
              <div className="student-section flex flex-col gap-4">
                {/* 상단: 학생 목록 또는 선택된 학생 카드 + 테스트 목록 */}
                <StudentSection
                  userType={userType}
                  navigateToStudent={navigateToStudent}
                  navigateToStudentList={navigateToStudentList}
                  onStudentDetailClick={handleStudentDetailClick}
                  onSelectedStudentChange={handleSelectedStudentChange}
                  isMobile={false}
                  isStudentSelected={isStudentSelected}
                  selectedStudent={selectedStudent}
                  studentListRef={studentListRef}
                  // 테스트 관련 props
                  testHistory={testHistory}
                  selectedTest={selectedTest}
                  isTestHistoryLoading={isTestHistoryLoading}
                  hasMoreTests={hasMoreTests}
                  onTestSelect={(test) => enhancedNavigateToTest(selectedStudent, test)}
                  testHistoryRef={testHistoryRef}
                  setCurrentMobilePanel={setCurrentMobilePanel}
                  loadMoreTests={loadMoreTests}
                />
              </div>

              {/* 우측 영역 - 폼레이팅 변화추이 및 분석 정보 (확장됨, 75%) */}
              <div className="analysis-section grid grid-cols-2 gap-4 overflow-hidden rounded-3xl bg-white shadow-lg" style={{ gridTemplateRows: 'auto auto 1fr' }}>
                {selectedStudent ? (
                  <>


                    {/* 난이도별 분석 카드 - 2행 1열 */}
                    {selectedTest && (
                      <div className="rounded-3xl bg-white shadow-lg h-fit">
                        <div className="px-4 pt-4">
                          <div className="mb-2 flex items-center justify-between">
                            <h3 className="text-[14px] font-semibold">난이도별 분석</h3>
                            <div className="rounded text-[9px] font-medium text-gray-500">
                              TID {selectedTest.exam_id} | {selectedTest.tree_name}
                            </div>
                          </div>
                        </div>
                        <div className="px-[14.4px] pb-[14.4px]">
                          {/* 차트 영역 - 1:1 비율 */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* 난이도별 정답률 (1/2 영역) */}
                            <DifficultyAccuracyChartSection
                              selectedTest={selectedTest}
                              getLevelAchievementData={getLevelAchievementData}
                              getYAxisLabelColor={getYAxisLabelColor}
                            />

                            {/* 난이도별 성취도 (1/2 영역) */}
                            <DifficultyAchievementChartSection
                              selectedTest={selectedTest}
                              getLevelPieData={getLevelPieData}
                              renderActiveShape={renderActiveShape}
                              activePieIndex={activePieIndex}
                              setActivePieIndex={setActivePieIndex}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 문제 리스트 - 2행 2열과 3행 2열 연속 차지 (row-span-2) */}
                    {selectedTest && (
                      <div className="row-span-2 flex min-h-0 flex-col rounded-3xl bg-white p-4 shadow-lg overflow-hidden">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="text-[14px] font-bold text-gray-800">문제 리스트</h3>
                          <span className="text-[11px] font-bold">
                            <span className="text-primary">{selectedTest?.correct_count || 0}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-danger">{selectedTest?.wrong_count || 0}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-black">{(selectedTest?.correct_count || 0) + (selectedTest?.wrong_count || 0)}</span>
                          </span>
                          <Button
                            onClick={handlePrintExam}
                            variant="secondary"
                            className="!bg-gray-400 text-white hover:opacity-80"
                            size="xs"
                            rounded="3xl"
                            icon={<FileText size={12} />}
                            title="시험지 미리보기"
                          >
                            시험지
                          </Button>
                        </div>

                        {/* 카드 형태의 문제 미리보기 컴포넌트 - 데스크톱 */}
                        <div className="flex-1 min-h-0">
                          {selectedTest && (selectedTest.problems_list || selectedTest.sequence) ? (
                            <ProblemPreviewCards
                              problems={selectedTest.problems_list || selectedTest.sequence || []}
                              selectedTest={selectedTest}
                              onViewProblem={handleOpenProblemPreview}
                              highlightedIndex={highlightState.problemIndex}
                              onHighlight={(index, problemData) => {
                                // 하이라이트 상태 한 번에 업데이트
                                dispatchHighlight({
                                  type: 'SET_ALL',
                                  payload: {
                                    problemIndex: index,
                                    difficulty: problemData.difficulty ? Number(problemData.difficulty) : null,
                                    address: problemData.address || null
                                  }
                                });
                              }}
                              getProblemResult={getProblemResult}
                              isDesktop={true}
                            />
                          ) : (
                            <div className="flex items-center justify-center py-8">
                              <p className="text-xs text-gray-500">문제 정보가 없습니다.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 결과맵 요약 - 3행 1열 */}
                    {selectedTest && resultMapData && (
                      <div className="flex min-h-0 flex-col rounded-3xl bg-white p-4 shadow-lg">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="text-[14px] font-bold text-gray-800">결과맵 요약</h3>
                              {selectedTest?.exam_status === 999 && (
                                <Button
                                  onClick={() => dispatchModal({ type: 'OPEN_MODAL', payload: 'report' })}
                                  className={`${getReportStatusInfo(selectedTest.report_status || 0).color} ${getReportStatusInfo(selectedTest.report_status || 0).textColor} hover:opacity-80`}
                                  size="xs"
                                  rounded="3xl"
                                  icon={<FileText size={12} />}
                                  title={getReportStatusInfo(selectedTest.report_status || 0).tooltip}
                                >
                                  리포트
                                </Button>
                              )}
                            </div>

                            <div className="relative flex min-h-0 flex-1 flex-col">
                              <div className="problem-list-table flex-1 overflow-y-auto rounded border border-gray-200" ref={resultMapTableRef}>
                                <table className="w-full table-fixed divide-y divide-gray-200">
                                  <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                                    <tr>
                                      <th className="bg-gray-50 p-2 text-left text-xs font-medium text-gray-500">
                                        <div className="flex items-center justify-between">
                                          <span>
                                            {['대단원', '소단원', '대유형', '중유형'][visibleHierarchyLevel]}
                                          </span>
                                          <div className="ml-2 flex gap-1">
                                            <button
                                              onClick={() => handleColumnSwitch('left')}
                                              className="rounded p-0.5 hover:bg-gray-200"
                                              title="이전 유형"
                                            >
                                              <ChevronLeft size={12} />
                                            </button>
                                            <button
                                              onClick={() => handleColumnSwitch('right')}
                                              className="rounded p-0.5 hover:bg-gray-200"
                                              title="다음 유형"
                                            >
                                              <ChevronRight size={12} />
                                            </button>
                                          </div>
                                        </div>
                                      </th>
                                      <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 1 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>1</th>
                                      <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 2 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>2</th>
                                      <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 3 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>3</th>
                                      <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 4 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>4</th>
                                      <th className={`w-5 py-2 text-center text-[10px] ${highlightState.difficulty === 5 ? 'font-bold text-green-600' : 'font-normal text-gray-500'}`}>5</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {(resultMapList || []).map((resultMapItem, index) => {
                                      const currentColumnKey = ['d1Name', 'd2Name', 'd3Name', 'd4Name'][visibleHierarchyLevel];

                                      if (!resultMapItem) return null;

                                      return (
                                        <tr
                                          key={index}
                                          ref={el => rowRefs.current[resultMapItem.address] = el}
                                          className={`${index % 2 === 0 ? 'bg-gray-50' : ''} ${(resultMapItem.address === highlightState.address && highlightState.address) ? 'bg-green-200' : ''}`}
                                        >
                                          <td
                                            className="max-w-0 truncate p-2 text-xs text-gray-900"
                                            title={resultMapItem[currentColumnKey]?.replace(/<[^>]*>/g, '') || ''}
                                          >
                                            <div className="truncate" dangerouslySetInnerHTML={{ __html: resultMapItem[currentColumnKey] || '' }} />
                                          </td>
                                          {[0, 1, 2, 3, 4].map(levelIndex => (
                                            <td key={levelIndex} className="w-5 py-2 text-center">
                                              <div className="flex justify-center">
                                                <div
                                                  className={`size-3 rounded ${getStatusColor(resultMapItem.levels?.[levelIndex] || 0)} ${(resultMapItem.levels?.[levelIndex] || 0) === 0 ? 'border border-gray-300' : ''} ${resultMapItem.address === highlightState.address && highlightState.difficulty === (levelIndex + 1) && highlightState.address ? 'ring-4 ring-blue-600 ring-offset-1' : ''}`}
                                                  title={`Level ${levelIndex + 1}: ${resultMapItem.levels?.[levelIndex] || 0}`}
                                                >
                                                  {(resultMapItem.levels?.[levelIndex] || 0) === 0 && (
                                                    <span className="text-xs leading-3 text-gray-500">×</span>
                                                  )}
                                                </div>
                                              </div>
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                    {/* 폼레이팅 변화 추이 - DOM 순서는 마지막이지만 Grid로 1행에 위치 */}
                    <div className="col-span-2" style={{ gridRow: 1, gridColumn: '1 / span 2' }}>
                      <FormRatingChartSection
                        selectedStudent={selectedStudent}
                        selectedTest={selectedTest}
                        getExamChartData={getExamChartData}
                        getYAxisDomain={getYAxisDomain}
                        reportData={reportData}
                        onTestSelect={(test) => enhancedNavigateToTest(selectedStudent, test)}
                        testHistory={testHistory}
                      />
                    </div>

                  </>
                ) : (
                  // 학생이 선택되지 않은 경우 안내 메시지
                  <div className="col-span-2 row-span-3 flex items-center justify-center">
                    <p className="text-gray-500">좌측에서 학생을 선택하세요</p>
                  </div>
                )}
              </div>
            </div>
          )
        }
        </>
      )}
    />
      )}

    {/* 모든 모달들을 BypassLayout 밖으로 이동 */}

    {/* 학생 상세 정보 모달 */}
    <Modal
      isOpen={showDetailModal && selectedStudentDetail}
      onClose={() => setShowDetailModal(false)}
      title={`${selectedStudentDetail?.student_name || ''} 학생 상세 정보`}
      size="md"
      className={isMobileView ? "mx-2" : ""}
      footer={
        <div className="flex w-full justify-between">
          <div className="flex gap-2">
            {userType === 'manager' && (
              <Button
                variant="danger"
                onClick={() => dispatchModal({ type: 'OPEN_MODAL', payload: 'deleteConfirm' })}
                size={isMobileView ? "sm" : "md"}
              >
                삭제
              </Button>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowDetailModal(false)}
            size={isMobileView ? "sm" : "md"}
          >
            닫기
          </Button>
        </div>
      }
    >
      {isLoadingStudentDetail ? (
        <div className="flex h-48 items-center justify-center">
          <LoadingSpinner />
          <span className="ml-2 text-gray-600">최신 정보를 불러오는 중...</span>
        </div>
      ) : selectedStudentDetail ? (
        <>
          <div className="mb-4 flex justify-end gap-2">
            <Button
              variant="danger"
              size={isMobileView ? "xs" : "sm"}
              onClick={() => dispatchModal({ type: 'OPEN_MODAL', payload: 'resetPassword' })}
            >
              비밀번호 리셋
            </Button>
            {userType === 'manager' && (
              <Button
                variant="primary"
                size={isMobileView ? "xs" : "sm"}
                onClick={() => {
                  setShowDetailModal(false);
                  dispatchModal({ type: 'OPEN_MODAL', payload: 'teacherAssign' });
                  fetchTeachers();
                }}
              >
                강사 배정
              </Button>
            )}
          </div>
          <div className={`space-y-3 ${isMobileView ? 'text-sm' : ''}`}>
            <div className={`grid grid-cols-[1fr,2fr] gap-4 ${isMobileView ? 'gap-2' : ''}`}>
              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>이름:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.student_name}
                {userType === 'manager' && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      // console.log('Edit button clicked for student_name');
                      // console.log('selectedStudentDetail:', selectedStudentDetail);
                      handleModalOpen('edit', selectedStudentDetail, 'student_name');
                      // 상세 정보 모달은 닫지 않음 (수정 모달이 그 위에 뜨도록)
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>강의:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.lecture || '-'}
                {/* 학교·학년·강의 연필은 학생 v2 전환 전에만 — 전환 뒤에는 학생 v2 에서만 기록하고 서버가 v1 수정을 409 로 거절한다(결정 18 · ACADEMY_CUTOVER, docs/student-admin-v2/backend/BE-20261014-cutover-flag.md) */}
                {userType === 'manager' && !ACADEMY_CUTOVER && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalOpen('edit', selectedStudentDetail, 'lecture');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>이메일:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.student_email}
                {userType === 'manager' && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      // console.log('Edit button clicked for student_email');
                      handleModalOpen('edit', selectedStudentDetail, 'student_email');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>전화번호:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.mobile_number || '-'}
                {userType === 'manager' && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      // console.log('Edit button clicked for mobile_number');
                      handleModalOpen('edit', selectedStudentDetail, 'mobile_number');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>학부모 전화번호:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.parent_mobile_number || '-'}
                {userType === 'manager' && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      // console.log('Edit button clicked for parent_mobile_number');
                      handleModalOpen('edit', selectedStudentDetail, 'parent_mobile_number');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>학교:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.school_name}
                {userType === 'manager' && !ACADEMY_CUTOVER && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalOpen('edit', selectedStudentDetail, 'school_name');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>학년:</div>
              <div className={`group flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.school_grade}
                {userType === 'manager' && !ACADEMY_CUTOVER && (
                  <PenSquare 
                    className="ml-2 hidden size-4 cursor-pointer text-primary group-hover:inline-block"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalOpen('edit', selectedStudentDetail, 'school_grade');
                    }}
                  />
                )}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>유입 경로:</div>
              <div className={`flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.acq_channel || '-'}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>추천인:</div>
              <div className={`flex items-center ${isMobileView ? 'text-xs' : ''}`}>
                {selectedStudentDetail.referral_id || '-'}
              </div>

              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>등록일:</div>
              <div className={isMobileView ? 'text-xs' : ''}>{selectedStudentDetail?.created_at ? new Date(selectedStudentDetail.created_at).toLocaleDateString() : '-'}</div>
              
              <div className={`flex items-center font-semibold ${isMobileView ? 'text-xs' : ''}`}>수정일:</div>
              <div className={isMobileView ? 'text-xs' : ''}>{selectedStudentDetail?.updated_at ? new Date(selectedStudentDetail.updated_at).toLocaleDateString() : '-'}</div>
            </div>
          </div>
        </>
      ) : null}
    </Modal>

    {/* 문제 보기 모달 */}
    <Suspense fallback={<LoadingSpinner />}>
    <ProblemPreview 
      isOpen={modalState.problemPreview}
      onClose={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'problemPreview' })}
      problemIds={previewState.problemId}
      showNumbers={true}
      correctList={previewState.correct}
      problemNumbers={previewState.number}
    />
    </Suspense>

    {/* 시험지 미리보기 모달 */}
    <Suspense fallback={<LoadingSpinner />}>
    <PrintPreviewModal
      isOpen={modalState.printPreview}
      onClose={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'printPreview' })}
      testData={{
        ...selectedTest,
        student_name: selectedStudent?.student_name
      }}
      isMobileView={isMobileView}
    />
    </Suspense>

    {/* 카이리포트 모달 */}
    {selectedTest && (
      <Suspense fallback={<LoadingSpinner />}>
      <ReportModal
        isOpen={modalState.report}
        onClose={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'report' })}
        examData={{
          stamp_id: selectedTest.stamp_id,
          exam_id: selectedTest.exam_id
        }}
      />
      </Suspense>
    )}

    {/* 강사 배정 모달 */}
    <Modal
      isOpen={modalState.teacherAssign}
      onClose={handleTeacherModalClose}
      title="강사 할당"
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="danger"
            onClick={() => handleAllocateTeacher(selectedStudentDetail?.student_id, null)}
          >
            할당 삭제
          </Button>
          <Button
            variant="secondary"
            onClick={handleTeacherModalClose}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => handleAllocateTeacher(selectedStudentDetail?.student_id, studentModalState.selectedTeacherId)}
            disabled={!studentModalState.selectedTeacherId}
          >
            할당
          </Button>
        </div>
      }
    >
      <select
        className="w-full rounded border p-2 focus:border-primary focus:ring-primary"
        value={studentModalState.selectedTeacherId}
        onChange={(e) => setStudentModalState({ selectedTeacherId: e.target.value })}
      >
        <option value="">강사를 선택하세요</option>
        {studentModalState.teachers.map((teacher) => (
          <option key={teacher.user_id} value={teacher.user_id}>
            {teacher.user_name} ({teacher.charge_work})
          </option>
        ))}
      </select>
    </Modal>

    {/* 비밀번호 초기화 확인 모달 */}
    <ConfirmModal
      isOpen={modalState.resetPassword}
      onClose={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'resetPassword' })}
      onConfirm={async () => {
        try {
          const newPw = await handleResetPassword(selectedStudentDetail.student_id);
          setStudentModalState({ newPassword: newPw });
          dispatchModal({ type: 'CLOSE_MODAL', payload: 'resetPassword' });
          dispatchModal({ type: 'OPEN_MODAL', payload: 'passwordPopup' });
        } catch (error) {
          alert(error.message);
        }
      }}
      title="비밀번호 초기화"
      message="정말 비밀번호를 초기화하시겠습니까?"
      confirmText="확인"
      cancelText="취소"
      confirmVariant="danger"
    />

    {/* 새 비밀번호 표시 모달 */}
    <Modal
      isOpen={modalState.passwordPopup}
      onClose={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'passwordPopup' })}
      title="새 비밀번호"
      size="sm"
      footer={
        <Button
          variant="secondary"
          onClick={() => dispatchModal({ type: 'CLOSE_MODAL', payload: 'passwordPopup' })}
        >
          닫기
        </Button>
      }
    >
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={studentModalState.newPassword}
          readOnly
          className="flex-1 rounded border bg-gray-100 px-3 py-2"
        />
        <Button
          variant="primary"
          onClick={() => {
            navigator.clipboard.writeText(studentModalState.newPassword);
            alert('비밀번호가 클립보드에 복사되었습니다.');
          }}
        >
          복사
        </Button>
      </div>
    </Modal>

    {/* 학생 삭제 확인 모달 */}
    <Modal
      isOpen={modalState.deleteConfirm}
      onClose={() => {
        dispatchModal({ type: 'CLOSE_MODAL', payload: 'deleteConfirm' });
        setStudentModalState({ deleteEmail: '', deleteError: '' });
      }}
      title="학생 삭제"
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              dispatchModal({ type: 'CLOSE_MODAL', payload: 'deleteConfirm' });
              setStudentModalState({ deleteEmail: '', deleteError: '' });
            }}
          >
            취소
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
          >
            삭제
          </Button>
        </div>
      }
    >
      <p className="mb-4">삭제를 확인하려면 학생의 이메일을 입력하세요:</p>
      <p className="mb-2 text-sm text-gray-600">{selectedStudentDetail?.student_email}</p>
      <input
        type="email"
        value={studentModalState.deleteEmail}
        onChange={(e) => setStudentModalState({ deleteEmail: e.target.value })}
        className="mb-4 w-full rounded border px-3 py-2 focus:border-primary focus:ring-primary"
        placeholder="이메일 입력"
      />
      {studentModalState.deleteError && <p className="text-sm text-danger">{studentModalState.deleteError}</p>}
    </Modal>

    {/* 학생 정보 수정 모달 */}
    {/* console.log('StudentModal render check:', { isModalOpen, modalMode, editingStudent }) */}
    <Suspense fallback={<LoadingSpinner />}>
    <StudentModal
      isOpen={isModalOpen}
      onClose={handleModalClose}
      onSubmit={handleEditStudent}
      initialData={editingStudent}
      mode={modalMode}
      selectedStudentDetail={selectedStudentDetail}
    />
    </Suspense>
    </>
  );
}

export default React.memo(LearningStatus); 